import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import DashboardOverview from '../components/dashboard/DashboardOverview';
import TodayTasksPanel from '../components/dashboard/TodayTasksPanel';
import OnShiftPanel from '../components/dashboard/OnShiftPanel';
import HomeStatsPanel from '../components/dashboard/HomeStatsPanel';
import './AdminDashboard.css';

// Ključ za čuvanje pozicije skrolovanja dashboard-a između navigacija
// (npr. odlazak na rezidenta radi rešavanja akcije i povratak nazad)
const SCROLL_STORAGE_KEY = 'dashboard:admin:scrollTop';

// PC prikaz (dve kolone) se uključuje od ove širine; ispod je mobilni (jedna kolona)
const PC_BREAKPOINT = 1024;

// Pronađi element koji je stvarno skrolabilan (overflow + sadržaj)
const getScrollable = () => {
  const selectors = ['.admin-dashboard', '.adash-page', '.cds--content', '.page-container'];
  for (const sel of selectors) {
    const el = document.querySelector(sel);
    if (el && el.scrollHeight > el.clientHeight) return el;
  }
  return document.scrollingElement || document.documentElement;
};

// Izvuci `data` payload iz axios odgovora (ApiResponse<T> omotač ili goli payload)
const unwrapResponse = (response) => {
  const body = response?.data;
  return body && body.data !== undefined ? body.data : body;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= PC_BREAKPOINT);

  // Podaci za KPI kartice i panel smene
  const [summary, setSummary] = useState({
    loading: true,
    residents: null,
    shifts: null,
    areas: null,
    nextShift: null,
    tasks: null,
    actions: null,
  });
  // Inkrementira se klikom na KPI "U smeni" - otvara panel i skroluje do njega
  const [shiftOpenToken, setShiftOpenToken] = useState(0);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= PC_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sačuvaj poziciju skrolovanja pri odlasku i vrati je pri povratku na dashboard.
  // Sadržaj se učitava asinhrono, pa se pozicija vraća u nekoliko pokušaja dok se
  // visina sadržaja ne stabilizuje.
  useEffect(() => {
    let saved = 0;
    try {
      saved = Number(sessionStorage.getItem(SCROLL_STORAGE_KEY) || 0);
    } catch (e) {
      saved = 0;
    }

    let rafId = null;
    let attempts = 0;
    let cancelled = false;
    const maxAttempts = 90; // ~1.5s pri 60fps

    const cancelRestore = () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
    };

    const restore = () => {
      if (cancelled || saved <= 0) return;
      const el = getScrollable();
      if (el) el.scrollTop = saved;
      window.scrollTo(0, saved);

      const current = (el ? el.scrollTop : 0) || window.scrollY || 0;
      if (Math.abs(current - saved) > 2 && attempts < maxAttempts) {
        attempts += 1;
        rafId = requestAnimationFrame(restore);
      }
    };

    if (saved > 0) {
      rafId = requestAnimationFrame(restore);
    }

    const save = () => {
      const el = getScrollable();
      const top = (el ? el.scrollTop : 0) || window.scrollY || 0;
      try {
        sessionStorage.setItem(SCROLL_STORAGE_KEY, String(top));
      } catch (e) { /* ignore */ }
    };

    // Prekini vraćanje pozicije ako korisnik sam počne da skroluje
    window.addEventListener('wheel', cancelRestore, { passive: true });
    window.addEventListener('touchstart', cancelRestore, { passive: true });
    window.addEventListener('keydown', cancelRestore);

    window.addEventListener('scroll', save, { passive: true });
    document.addEventListener('scroll', save, { passive: true, capture: true });

    return () => {
      cancelRestore();
      save();
      window.removeEventListener('wheel', cancelRestore);
      window.removeEventListener('touchstart', cancelRestore);
      window.removeEventListener('keydown', cancelRestore);
      window.removeEventListener('scroll', save);
      document.removeEventListener('scroll', save, { capture: true });
    };
  }, []);

  // Učitaj sažetak za KPI kartice i panel smene — JEDAN agregatni poziv.
  // Ranije je dashboard radio 6 paralelnih zahteva, pa je svaki plaćao auth + RLS transakciju.
  // Server (/api/Dashboard/admin) vraća samo blokove na koje korisnik ima permisiju;
  // ostali su null i paneli se jednostavno ne prikazuju.
  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      let payload = {};
      try {
        const response = await axiosInstance.get('/api/Dashboard/admin');
        payload = unwrapResponse(response) || {};
      } catch (error) {
        // Dashboard ne sme da ostane u "loading" stanju ako agregat padne.
        payload = {};
      }

      if (!isMounted) return;
      setSummary({
        loading: false,
        residents: payload.residents ?? null,
        shifts: payload.currentShifts ?? null,
        areas: payload.areas ?? null,
        nextShift: payload.nextShift ?? null,
        tasks: payload.taskStats ?? null,
        actions: Array.isArray(payload.actions) ? payload.actions : [],
      });
    };

    load();
    return () => {
      isMounted = false;
    };
  }, []);

  const { residents, shifts, areas, nextShift, tasks, actions } = summary;
  const taskAreas = Array.isArray(tasks?.areas) ? tasks.areas : [];
  const completedTotal = taskAreas.reduce((sum, area) => sum + (area.completedTasks || 0), 0);
  const unassignedTotal = taskAreas.reduce((sum, area) => sum + (area.unassignedTasks || 0), 0);
  const actionsList = Array.isArray(actions) ? actions : [];

  // Mapa "naziv oblasti -> areaId" iz GET /api/Area. Koristi se da se ispod svake
  // oblasti u panelu "Trenutno u smeni" prikažu beleške smene za tu oblast
  // (učitavaju se preko GET /api/ShiftNote/area-today/{areaId}?pageNumber=&pageSize=5).
  const areaIdsByArea = useMemo(() => {
    const list = Array.isArray(areas)
      ? areas
      : Array.isArray(areas?.areas)
      ? areas.areas
      : [];
    if (list.length === 0) return null;

    const map = {};
    list.forEach((area) => {
      if (area?.id && area?.name) map[area.name] = area.id;
    });
    return Object.keys(map).length > 0 ? map : null;
  }, [areas]);

  const kpiStats = {
    occupancyPercentage: residents?.occupancyPercentage ?? 0,
    takenBeds: residents?.takenBeds ?? 0,
    totalBeds: residents?.totalBeds ?? 0,
    employeesOnShift: shifts?.totalEmployeesOnShift ?? 0,
    areasOnShift: shifts?.totalAreas ?? (Array.isArray(shifts?.areas) ? shifts.areas.length : 0),
    totalTasks: tasks?.totalTasks ?? 0,
    completedTasks: completedTotal,
    unassignedTasks: unassignedTotal,
    actionsCount: actionsList.length,
    criticalActions: actionsList.filter((a) => a.severity === 'Critical').length,
    warningActions: actionsList.filter((a) => a.severity === 'Warning').length,
  };

  // Klik na KPI karticu "U smeni": otvori panel "Trenutno u smeni" i skroluj skroz do dole
  const handleShiftClick = () => {
    setShiftOpenToken((token) => token + 1);

    // Sačekaj da se panel otvori (Collapsible ~0.15s) pa skroluj do dna stranice
    const scrollToBottom = () => {
      const doc = document.scrollingElement || document.documentElement;
      window.scrollTo({ top: doc.scrollHeight, behavior: 'smooth' });
    };

    window.setTimeout(scrollToBottom, 220);
    // Ponovi kratko nakon animacije (npr. ako slike promene visinu sadržaja)
    window.setTimeout(scrollToBottom, 520);
  };

  return (
    <div className="admin-dashboard" style={{ overflowX: 'hidden' }}>
      <div className={`adash-page ${isDesktop ? 'adash-page--pc' : 'adash-page--mobile'}`}>
        <DashboardOverview
          stats={kpiStats}
          onShiftClick={handleShiftClick}
          onActionsClick={() => navigate('/panel-doma?tab=actions')}
        />

        {isDesktop ? (
          /* PC: dve kolone - Zadaci (levo) i Smena (desno) */
          <div className="adash-grid">
            <TodayTasksPanel onNavigateTasks={() => navigate('/tasks')} />
            <div className="adash-shift-col">
              <OnShiftPanel
                data={shifts}
                loading={summary.loading}
                openToken={shiftOpenToken}
                areaIdsByArea={areaIdsByArea}
              />
              <OnShiftPanel
                data={nextShift}
                loading={summary.loading}
                title="Sledeća smena"
                emptyText="Nema predviđenih zaposlenih za sledeću smenu"
                sectionId="adash-next-shift"
              />
            </div>
          </div>
        ) : (
          /* Mobilni: sve u jednoj koloni, paneli skupljeni po defaultu */
          <>
            <TodayTasksPanel onNavigateTasks={() => navigate('/tasks')} />
            <OnShiftPanel
              data={shifts}
              loading={summary.loading}
              defaultCollapsed
              openToken={shiftOpenToken}
              areaIdsByArea={areaIdsByArea}
            />
            <OnShiftPanel
              data={nextShift}
              loading={summary.loading}
              defaultCollapsed
              title="Sledeća smena"
              emptyText="Nema predviđenih zaposlenih za sledeću smenu"
              sectionId="adash-next-shift"
            />
            <HomeStatsPanel />
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
